import { PULSE_DISPLAY_TIMEZONE } from "./eventSchedule";

/** Venue category used for hours fallbacks (Clubs/Bars/Rooftop/Concerts). */
export type VenueHoursCategory = "Bars" | "Clubs" | "Rooftop" | "Concerts" | string;

const WEEKDAY_EN = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

const WEEKDAY_DE = [
  "Sonntag",
  "Montag",
  "Dienstag",
  "Mittwoch",
  "Donnerstag",
  "Freitag",
  "Samstag",
] as const;

/** Short German weekday labels for hours display (Sunday = index 0). */
const WEEKDAY_SHORT_DE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"] as const;

const CLOSED_PATTERN = /\b(closed|geschlossen)\b/i;
const OPEN_24_HOURS_PATTERN =
  /\b(open\s*24\s*hours?|24\s*hours?\s*open|open\s*all\s*day|rund\s*um\s*die\s*uhr|24\s*stunden(?:\s*geöffnet)?)\b/i;

const END_OF_DAY_MINUTES = 24 * 60;
const NIGHTLIFE_THRESHOLD = 16 * 60;
const ROOFTOP_THRESHOLD = 12 * 60;
const OPENS_SOON_MINUTES = 240;

/** Hardcoded hours when Google Places data is missing or wrong (matched by venue name). */
const TAOS_OPENING_HOURS = [
  "Monday: 17:00 – 00:00",
  "Tuesday: 17:00 – 00:00",
  "Wednesday: 17:00 – 00:00",
  "Thursday: 17:00 – 00:00",
  "Friday: 17:00 – 00:00",
  "Saturday: 17:00 – 00:00",
  "Sunday: Closed",
] as const;

// parseRangeString("2:00 – 11:00 PM")     → { open: 840, close: 1380 }  // 14:00–23:00
// parseRangeString("11:00 PM – 4:00 AM")  → { open: 1380, close: 240 }  // overnight
// parseRangeString("Open 24 hours")       → { open: 0, close: 1440 }
// parseRangeString("2:00 PM – 4:00 AM")   → { open: 840, close: 240 }   // overnight
// parseRangeString("9:00 AM – 3:00 PM")   → { open: 540, close: 900 }

export type VenueOpenStatus = "open" | "opens-later" | "closed";

type TimeRange = { open: number; close: number; label: string };

type DaySchedule =
  | { kind: "closed" }
  | { kind: "hours"; ranges: TimeRange[] };

/** Default `now = new Date()` does not apply when callers pass `undefined` explicitly. */
function coerceNow(now?: unknown): Date {
  return now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
}

/**
 * LRU-1 caches for Intl-heavy helpers keyed by `now.getTime()`.
 * Same tick within a render hits the cache; only the last key is retained.
 * Cache stores the successful final result (including weekday fallback path).
 */
type WeekdayNamesResult = { en: string; de: string; index: number };
let todayWeekdayNamesCacheKey: number | null = null;
let todayWeekdayNamesCacheValue: WeekdayNamesResult | null = null;
let minutesInZurichCacheKey: number | null = null;
let minutesInZurichCacheValue: number | null = null;

let todayWeekdayNamesHits = 0;
let todayWeekdayNamesMisses = 0;
let minutesInZurichHits = 0;
let minutesInZurichMisses = 0;

/** DEV/measurement: Intl helper LRU-1 hit/miss counters. */
export function getVenueHoursIntlCacheStatsForTests(): {
  todayWeekdayNames: { hits: number; misses: number };
  minutesInZurich: { hits: number; misses: number };
} {
  return {
    todayWeekdayNames: { hits: todayWeekdayNamesHits, misses: todayWeekdayNamesMisses },
    minutesInZurich: { hits: minutesInZurichHits, misses: minutesInZurichMisses },
  };
}

/** DEV/measurement: reset Intl helper cache counters (does not clear cached values). */
export function resetVenueHoursIntlCacheStatsForTests(): void {
  todayWeekdayNamesHits = 0;
  todayWeekdayNamesMisses = 0;
  minutesInZurichHits = 0;
  minutesInZurichMisses = 0;
}

function publishVenueHoursIntlCacheApi(): void {
  const g = globalThis as typeof globalThis & {
    __pulseVenueHoursIntlCache?: {
      stats: typeof getVenueHoursIntlCacheStatsForTests;
      reset: typeof resetVenueHoursIntlCacheStatsForTests;
    };
  };
  g.__pulseVenueHoursIntlCache = {
    stats: getVenueHoursIntlCacheStatsForTests,
    reset: resetVenueHoursIntlCacheStatsForTests,
  };
}
publishVenueHoursIntlCacheApi();

function todayWeekdayNames(now: Date = new Date()): WeekdayNamesResult {
  const currentTime = coerceNow(now);
  const key = currentTime.getTime();
  if (
    todayWeekdayNamesCacheKey === key &&
    todayWeekdayNamesCacheValue != null
  ) {
    todayWeekdayNamesHits += 1;
    return todayWeekdayNamesCacheValue;
  }

  todayWeekdayNamesMisses += 1;
  const day = currentTime.toLocaleDateString("en-US", { weekday: "long", timeZone: PULSE_DISPLAY_TIMEZONE });
  const deDay = currentTime.toLocaleDateString("de-CH", { weekday: "long", timeZone: PULSE_DISPLAY_TIMEZONE });
  let index = WEEKDAY_EN.indexOf(day as (typeof WEEKDAY_EN)[number]);
  // indexOf === -1 if the locale label ever diverges from WEEKDAY_EN (engine/ICU quirk).
  // That would otherwise flow into weekdayAtIndex and yield undefined open/closed labels.
  if (index < 0) {
    const zurichWall = new Date(
      currentTime.toLocaleString("en-US", { timeZone: PULSE_DISPLAY_TIMEZONE }),
    );
    index = Number.isNaN(zurichWall.getTime()) ? 0 : zurichWall.getDay();
    console.warn("[venueHours] Unexpected weekday label from locale; using Zurich getDay fallback", {
      day,
      deDay,
      timeZone: PULSE_DISPLAY_TIMEZONE,
      fallbackIndex: index,
    });
  }
  // Cache the successful final result only (never a mid-fallback partial).
  const result: WeekdayNamesResult = {
    en: day,
    de: deDay.charAt(0).toUpperCase() + deDay.slice(1),
    index,
  };
  todayWeekdayNamesCacheKey = key;
  todayWeekdayNamesCacheValue = result;
  return result;
}

function minutesInZurich(now: unknown = new Date()): number {
  const currentTime = coerceNow(now);
  const key = currentTime.getTime();
  if (
    minutesInZurichCacheKey === key &&
    minutesInZurichCacheValue != null
  ) {
    minutesInZurichHits += 1;
    return minutesInZurichCacheValue;
  }

  minutesInZurichMisses += 1;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: PULSE_DISPLAY_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(currentTime);
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
  const result = hour * 60 + minute;
  minutesInZurichCacheKey = key;
  minutesInZurichCacheValue = result;
  return result;
}

function normalizeLine(line: string): string {
  return line.trim().replace(/\u00a0/g, " ");
}

const WEEKDAY_NAME_PATTERN = [...WEEKDAY_EN, ...WEEKDAY_DE].join("|");

/**
 * Strip leading weekday so hours parsing never treats the colon inside
 * "08:00" as the "Day: value" separator.
 *
 * Supports both canonical Google-style lines (`Friday: 08:00–02:00`) and
 * malformed stored lines that use tab/space instead of `:` (`Friday\t08:00–02:00`).
 */
function extractHoursValueFromLine(line: string): string {
  const normalized = normalizeLine(line);
  const weekdayPrefixed = normalized.match(
    new RegExp(`^(?:${WEEKDAY_NAME_PATTERN})(?:\\s*[:.]\\s*|\\s+)(.+)$`, "i"),
  );
  if (weekdayPrefixed?.[1]) return weekdayPrefixed[1].trim();
  return normalized;
}

function lineMatchesWeekday(
  line: string,
  weekday: { en: string; de: string },
): boolean {
  const normalized = normalizeLine(line);
  const lower = normalized.toLowerCase();
  return (
    lower.startsWith(weekday.en.toLowerCase()) ||
    lower.startsWith(weekday.de.toLowerCase())
  );
}

/** Format minutes since midnight as 24-hour clock (e.g. 14:00). */
export function minutesToTime24(minutes: number): string {
  const totalMins = ((minutes % 1440) + 1440) % 1440;
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

export function formatRangeLabel(open: number, close: number): string {
  return `${minutesToTime24(open)} – ${minutesToTime24(close)}`;
}

function parseTimeToMinutes(timeStr: string, defaultPeriod?: "AM" | "PM"): number {
  let trimmed = timeStr.trim().replace(/[,.]$/, "");

  const amPmMatch = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (amPmMatch) {
    let h = Number(amPmMatch[1]);
    const m = Number(amPmMatch[2]);
    const period = amPmMatch[3]!.toUpperCase();
    if (period === "PM" && h !== 12) h += 12;
    if (period === "AM" && h === 12) h = 0;
    return h * 60 + m;
  }

  const timeMatch = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (timeMatch) {
    let h = Number(timeMatch[1]);
    const m = Number(timeMatch[2]);
    if (defaultPeriod) {
      if (defaultPeriod === "PM" && h !== 12) h += 12;
      if (defaultPeriod === "AM" && h === 12) h = 0;
    }
    if (h >= 24 || m >= 60) {
      console.warn("[venueHours] Could not parse time:", timeStr);
      return -1;
    }
    return h * 60 + m;
  }

  console.warn("[venueHours] Could not parse time:", timeStr);
  return -1;
}

export function parseRangeString(rangeStr: string): { open: number; close: number } | null {
  const normalized = rangeStr.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();
  if (!normalized || CLOSED_PATTERN.test(normalized)) return null;

  if (OPEN_24_HOURS_PATTERN.test(normalized)) {
    return { open: 0, close: END_OF_DAY_MINUTES };
  }

  const parts = normalized.split(/\s*[–—-]\s*/);
  if (parts.length !== 2) {
    console.warn("[venueHours] Could not split range:", rangeStr);
    return null;
  }

  const [openStr, closeStr] = parts;
  const closePeriod = closeStr!.match(/\b(AM|PM)\b/i)?.[0]?.toUpperCase() as "AM" | "PM" | undefined;
  const openPeriod = openStr!.match(/\b(AM|PM)\b/i)?.[0]?.toUpperCase() as "AM" | "PM" | undefined;

  let openMinutes: number;
  if (openPeriod) {
    openMinutes = parseTimeToMinutes(openStr!);
  } else if (closePeriod) {
    openMinutes = parseTimeToMinutes(openStr!, closePeriod);
  } else {
    openMinutes = parseTimeToMinutes(openStr!);
  }

  let closeMinutes: number;
  if (closePeriod) {
    closeMinutes = parseTimeToMinutes(closeStr!);
  } else if (openPeriod) {
    closeMinutes = parseTimeToMinutes(closeStr!, openPeriod);
  } else {
    closeMinutes = parseTimeToMinutes(closeStr!);
  }

  if (openMinutes < 0 || closeMinutes < 0) {
    console.warn("[venueHours] Invalid range open/close:", rangeStr);
    return null;
  }

  return { open: openMinutes, close: closeMinutes };
}

/** Parse a hours fragment like "5:00 PM – 12:00 AM", "22:00–05:00", or "Open 24 hours". */
export function parseHoursString(
  value: string,
): { open: number; close: number; label: string } | null {
  const normalized = value.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();
  if (!normalized || CLOSED_PATTERN.test(normalized)) return null;

  if (OPEN_24_HOURS_PATTERN.test(normalized)) {
    return { open: 0, close: END_OF_DAY_MINUTES, label: "Open 24 hours" };
  }

  const parsed = parseRangeString(normalized);
  if (!parsed) return null;
  return { ...parsed, label: formatRangeLabel(parsed.open, parsed.close) };
}

function parseAllRanges(value: string): TimeRange[] {
  const normalized = value.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();
  if (!normalized || CLOSED_PATTERN.test(normalized)) return [];

  if (OPEN_24_HOURS_PATTERN.test(normalized)) {
    return [{ open: 0, close: END_OF_DAY_MINUTES, label: "Open 24 hours" }];
  }

  const ranges: TimeRange[] = [];
  for (const part of normalized.split(",")) {
    const range = parseHoursString(part.trim());
    if (range) ranges.push(range);
  }

  if (ranges.length === 0) {
    const single = parseHoursString(normalized);
    if (single) return [single];
    console.warn("[venueHours] Could not parse hours value:", value);
  }

  return ranges;
}

function scheduleFromLine(line: string): DaySchedule {
  const value = extractHoursValueFromLine(line);
  if (CLOSED_PATTERN.test(value)) return { kind: "closed" };
  const ranges = parseAllRanges(value);
  if (ranges.length === 0) {
    if (value.trim().length > 0) {
      console.warn("[venueHours] Malformed hours line:", line);
    }
    return { kind: "closed" };
  }
  return { kind: "hours", ranges };
}

function weekdayAtIndex(index: number): { en: string; de: string } {
  // Defense in depth: callers normally pass 0..6 from todayWeekdayNames / (i+6)%7.
  // Out-of-range here historically meant undefined EN/DE labels → wrong open/closed matching.
  if (!Number.isInteger(index) || index < 0 || index > 6) {
    console.warn("[venueHours] weekday index out of range 0..6 — check caller / locale path", {
      index,
      // Not crawler-sourced today: indices come from Date/locale in this module.
      // If this fires repeatedly, investigate todayWeekdayNames indexOf failure or a bad caller.
    });
    const clamped = ((index % 7) + 7) % 7;
    return { en: WEEKDAY_EN[clamped]!, de: WEEKDAY_DE[clamped]! };
  }
  return { en: WEEKDAY_EN[index]!, de: WEEKDAY_DE[index]! };
}

function hasStoredHours(openingHours: string[] | null | undefined): boolean {
  return Array.isArray(openingHours) && openingHours.some((line) => line.trim().length > 0);
}

function isTaosVenue(venueName: string): boolean {
  const normalized = venueName.toLowerCase().replace(/['']/g, "'");
  return normalized.includes("tao") && (normalized.includes("restaurant") || normalized.includes("bar"));
}

function venueHoursFallbackLines(venueName?: string): readonly string[] | null {
  if (!venueName) return null;
  if (isTaosVenue(venueName)) return TAOS_OPENING_HOURS;
  return null;
}

/** Category defaults when opening_hours is completely empty. */
function fallbackHoursForWeekdayIndex(
  category: VenueHoursCategory | undefined,
  weekdayIndex: number,
): string | null {
  if (!category) return null;
  const day = weekdayAtIndex(weekdayIndex).en;

  switch (category) {
    case "Clubs":
      if (day === "Thursday" || day === "Friday" || day === "Saturday") return "22:00 – 05:00";
      return null;
    case "Bars":
      return "18:00 – 02:00";
    case "Rooftop":
      return "12:00 – 23:00";
    case "Concerts":
      return null;
    default:
      return null;
  }
}

function scheduleForWeekdayIndex(
  openingHours: string[] | null | undefined,
  weekdayIndex: number,
  category?: VenueHoursCategory,
  venueName?: string,
): DaySchedule {
  const weekday = weekdayAtIndex(weekdayIndex);
  const venueFallback = venueHoursFallbackLines(venueName);
  if (venueFallback) {
    for (const line of venueFallback) {
      if (!lineMatchesWeekday(line, weekday)) continue;
      return scheduleFromLine(line);
    }
    return { kind: "closed" };
  }

  if (hasStoredHours(openingHours)) {
    for (const line of openingHours!) {
      if (!lineMatchesWeekday(line, weekday)) continue;
      return scheduleFromLine(line);
    }
    return { kind: "closed" };
  }

  if (!category) return { kind: "closed" };

  console.warn("[venueHours] Using category fallback hours for", category, venueName ?? "");
  const fallback = fallbackHoursForWeekdayIndex(category, weekdayIndex);
  if (!fallback) return { kind: "closed" };
  const ranges = parseAllRanges(fallback);
  if (ranges.length === 0) return { kind: "closed" };
  return { kind: "hours", ranges };
}

function nightlifeStartMinutes(category?: VenueHoursCategory): number {
  return category === "Rooftop" ? ROOFTOP_THRESHOLD : NIGHTLIFE_THRESHOLD;
}

/** close=0 means midnight (end of day), not overnight spill into next morning. */
function isOvernight(open: number, close: number): boolean {
  return close !== 0 && close < open;
}

function isOpenInRange(nowMinutes: number, open: number, close: number): boolean {
  if (isOvernight(open, close)) {
    return nowMinutes >= open || nowMinutes < close;
  }

  const end = close === 0 ? END_OF_DAY_MINUTES : close;
  return nowMinutes >= open && nowMinutes < end;
}

function isOpenInRanges(nowMinutes: number, ranges: { open: number; close: number }[]): boolean {
  return ranges.some(({ open, close }) => isOpenInRange(nowMinutes, open, close));
}

/**
 * Ranges relevant "right now": today's schedule plus yesterday's overnight spillover
 * still active after midnight (e.g. Fri 23:00–07:00 still open Sat 00:35).
 */
function getTodayRanges(
  openingHours: string[] | null | undefined,
  category?: VenueHoursCategory,
  now: Date = new Date(),
  venueName?: string,
): { open: number; close: number }[] {
  const currentTime = coerceNow(now);
  const currentMinutes = minutesInZurich(currentTime);
  const { index: todayIndex } = todayWeekdayNames(currentTime);
  const yesterdayIndex = (todayIndex + 6) % 7;

  const todaySchedule = scheduleForWeekdayIndex(openingHours, todayIndex, category, venueName);
  const todayRanges =
    todaySchedule.kind === "hours"
      ? todaySchedule.ranges.map(({ open, close }) => ({ open, close }))
      : [];

  const yesterdaySchedule = scheduleForWeekdayIndex(
    openingHours,
    yesterdayIndex,
    category,
    venueName,
  );
  const yesterdayRanges =
    yesterdaySchedule.kind === "hours"
      ? yesterdaySchedule.ranges.map(({ open, close }) => ({ open, close }))
      : [];

  const spilloverRanges: { open: number; close: number }[] = [];
  for (const r of yesterdayRanges) {
    if (isOvernight(r.open, r.close) && currentMinutes < r.close) {
      spilloverRanges.push(r);
    }
  }

  return [...todayRanges, ...spilloverRanges];
}

/** Whether the venue is open at the current moment (Europe/Zurich). */
export function isOpenNow(
  openingHours: string[] | null | undefined,
  category?: VenueHoursCategory,
  now: Date = new Date(),
  venueName?: string,
): boolean {
  const currentTime = coerceNow(now);
  const nowMinutes = minutesInZurich(currentTime);
  const ranges = getTodayRanges(openingHours, category, currentTime, venueName);
  if (ranges.length === 0) return false;
  return isOpenInRanges(nowMinutes, ranges);
}

/** Whether the venue opens at any point today for nightlife (evening+; rooftop from noon). */
export function isOpenToday(
  openingHours: string[] | null | undefined,
  category?: VenueHoursCategory,
  now: Date = new Date(),
  venueName?: string,
): boolean {
  const currentTime = coerceNow(now);
  const ranges = getTodayRanges(openingHours, category, currentTime, venueName);
  if (ranges.length === 0) return false;

  const minMinutes = nightlifeStartMinutes(category);
  return ranges.some(({ open, close }) => open >= minMinutes || isOvernight(open, close));
}

export function getVenueOpenStatus(
  openingHours: string[] | null | undefined,
  category?: VenueHoursCategory,
  now: Date = new Date(),
  venueName?: string,
): VenueOpenStatus {
  const currentTime = coerceNow(now);
  if (isOpenNow(openingHours, category, currentTime, venueName)) {
    return "open";
  }

  const nowMinutes = minutesInZurich(currentTime);
  const { index: todayIndex } = todayWeekdayNames(currentTime);

  const todaySchedule = scheduleForWeekdayIndex(
    openingHours,
    todayIndex,
    category,
    venueName,
  );
  const allRanges = todaySchedule.kind === "hours" ? todaySchedule.ranges : [];

  const openingSoon = allRanges.some(({ open }) => {
    const minutesUntilOpen =
      open > nowMinutes ? open - nowMinutes : open + 1440 - nowMinutes;
    return minutesUntilOpen > 0 && minutesUntilOpen <= OPENS_SOON_MINUTES;
  });

  if (openingSoon) return "opens-later";
  return "closed";
}

function pickNightlifeRange(ranges: TimeRange[], category?: VenueHoursCategory): TimeRange | null {
  if (ranges.length === 0) return null;

  const minMinutes = nightlifeStartMinutes(category);
  const evening = ranges.find((r) => r.open >= minMinutes);
  if (evening) return evening;

  const overnight = ranges.find((r) => isOvernight(r.open, r.close));
  if (overnight) return overnight;

  return ranges[ranges.length - 1] ?? null;
}

function weekdayShortDe(index: number): string {
  return WEEKDAY_SHORT_DE[index] ?? WEEKDAY_SHORT_DE[0];
}

function formatOvernightSpilloverDisplay(
  yesterdayIndex: number,
  todayIndex: number,
  open: number,
  close: number,
): string {
  return `${weekdayShortDe(yesterdayIndex)} ${minutesToTime24(open)} – ${weekdayShortDe(todayIndex)} ${minutesToTime24(close)}`;
}

function formatTodayNightRangeDisplay(
  dayIndex: number,
  range: TimeRange,
  currentMinutes: number,
): string {
  if (range.label === "Open 24 hours") return "Open 24 hours";

  const { open, close } = range;
  const tomorrowIndex = (dayIndex + 1) % 7;

  if (isOvernight(open, close)) {
    return `${weekdayShortDe(dayIndex)} ${minutesToTime24(open)} – ${weekdayShortDe(tomorrowIndex)} ${minutesToTime24(close)}`;
  }

  const closeLabel = close === 0 ? "00:00" : minutesToTime24(close);

  if (close === 0) {
    if (currentMinutes >= open) {
      return `${minutesToTime24(open)} – 00:00`;
    }
    return `${weekdayShortDe(dayIndex)} ${minutesToTime24(open)} – ${weekdayShortDe(tomorrowIndex)} 00:00`;
  }

  return `${minutesToTime24(open)} – ${closeLabel}`;
}

function activeYesterdaySpilloverRange(
  openingHours: string[] | null | undefined,
  category: VenueHoursCategory | undefined,
  now: Date,
  venueName?: string,
): { open: number; close: number } | null {
  const currentMinutes = minutesInZurich(now);
  const yesterdayIndex = (todayWeekdayNames(now).index + 6) % 7;
  const yesterdaySchedule = scheduleForWeekdayIndex(
    openingHours,
    yesterdayIndex,
    category,
    venueName,
  );
  const yesterdayRanges =
    yesterdaySchedule.kind === "hours"
      ? yesterdaySchedule.ranges.map(({ open, close }) => ({ open, close }))
      : [];

  for (const r of yesterdayRanges) {
    if (
      isOvernight(r.open, r.close) &&
      currentMinutes < r.close &&
      isOpenInRange(currentMinutes, r.open, r.close)
    ) {
      return r;
    }
  }

  return null;
}

function buildVenueHoursDisplayString(
  openingHours: string[] | null | undefined,
  category: VenueHoursCategory | undefined,
  now: Date,
  venueName?: string,
): string {
  const currentMinutes = minutesInZurich(now);
  const { index: todayIndex } = todayWeekdayNames(now);
  const yesterdayIndex = (todayIndex + 6) % 7;

  const spillover = activeYesterdaySpilloverRange(
    openingHours,
    category,
    now,
    venueName,
  );
  if (spillover) {
    return formatOvernightSpilloverDisplay(
      yesterdayIndex,
      todayIndex,
      spillover.open,
      spillover.close,
    );
  }

  const today = scheduleForWeekdayIndex(openingHours, todayIndex, category, venueName);
  if (today.kind === "hours") {
    const nightRange = pickNightlifeRange(today.ranges, category);
    if (nightRange) {
      return formatTodayNightRangeDisplay(todayIndex, nightRange, currentMinutes);
    }
  }

  return "";
}

/** Hours string for display; shows yesterday spillover with day prefix when active. */
export function getVenueHoursDisplay(
  openingHours: string[] | null | undefined,
  category?: VenueHoursCategory,
  now: Date = new Date(),
  venueName?: string,
): string {
  const currentTime = coerceNow(now);
  return buildVenueHoursDisplayString(openingHours, category, currentTime, venueName);
}

/** Range that is currently keeping the venue open (today or yesterday spillover). */
function getActiveOpenRange(
  openingHours: string[] | null | undefined,
  category?: VenueHoursCategory,
  now: Date = new Date(),
  venueName?: string,
): { open: number; close: number } | null {
  const currentTime = coerceNow(now);
  const currentMinutes = minutesInZurich(currentTime);
  const ranges = getTodayRanges(openingHours, category, currentTime, venueName);

  for (const range of ranges) {
    if (isOpenInRange(currentMinutes, range.open, range.close)) {
      return range;
    }
  }

  return null;
}

function scheduledNightlifeRangeForToday(
  openingHours: string[] | null | undefined,
  category: VenueHoursCategory | undefined,
  now: Date,
  venueName?: string,
): TimeRange | null {
  const { index: todayIndex } = todayWeekdayNames(now);
  const today = scheduleForWeekdayIndex(openingHours, todayIndex, category, venueName);
  if (today.kind !== "hours") return null;
  return pickNightlifeRange(today.ranges, category);
}

/** Minutes from midnight (Europe/Zurich) when the venue opens today, or null if closed all day. */
export function getTodayOpeningMinutes(
  openingHours: string[] | null | undefined,
  category?: VenueHoursCategory,
  now: Date = new Date(),
  venueName?: string,
): number | null {
  const currentTime = coerceNow(now);

  const active = getActiveOpenRange(openingHours, category, currentTime, venueName);
  if (active) return active.open;

  const nightRange = scheduledNightlifeRangeForToday(
    openingHours,
    category,
    currentTime,
    venueName,
  );
  return nightRange?.open ?? null;
}

/** Latest closing time (minutes from midnight) for today's nightlife hours, or null if closed. */
export function getTodayClosingMinutes(
  openingHours: string[] | null | undefined,
  category?: VenueHoursCategory,
  now: Date = new Date(),
  venueName?: string,
): number | null {
  const currentTime = coerceNow(now);

  const active = getActiveOpenRange(openingHours, category, currentTime, venueName);
  if (active) return active.close;

  const nightRange = scheduledNightlifeRangeForToday(
    openingHours,
    category,
    currentTime,
    venueName,
  );
  return nightRange?.close ?? null;
}

/** Whether the venue is open at a given clock time (minutes from midnight) on today's schedule. */
export function isVenueOpenAtMinutes(
  openingHours: string[] | null | undefined,
  arrivalMinutes: number,
  category?: VenueHoursCategory,
  now: Date = new Date(),
  venueName?: string,
): boolean {
  const currentTime = coerceNow(now);
  const todayRanges = getTodayRanges(openingHours, category, currentTime, venueName);
  if (todayRanges.length === 0) return false;
  return isOpenInRanges(arrivalMinutes, todayRanges);
}
